import React from 'react';
import { HelpRichText } from './HelpRichText';
import type { HelpEntry } from './help-types';

export interface HelpVideoDetailsProps {
  entry: Pick<HelpEntry, 'id' | 'purpose' | 'flowText' | 'steps' | 'keyConcept' | 'video'>;
}

/**
 * What the playing video is about, from its Video script (no new help text): "What it does"
 * (Purpose, and a Flow's Flow text), "How to do it" (the Interim steps, with the exact UI
 * labels in bold) and the Key concept note. Without a published video the player itself
 * shows the steps under "Video coming soon", so they are not repeated here.
 */
export function HelpVideoDetails({ entry }: HelpVideoDetailsProps) {
  const showSteps = entry.video !== null && entry.steps.length > 0;
  return (
    <div className="space-y-4 text-sm" data-testid={`help-details-${entry.id}`}>
      {(entry.purpose || entry.flowText) && (
        <section aria-labelledby={`help-details-${entry.id}-what`} className="space-y-1.5">
          <h3 id={`help-details-${entry.id}-what`} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            What it does
          </h3>
          {entry.purpose && <p className="leading-relaxed text-foreground">{entry.purpose}</p>}
          {entry.flowText && <p className="leading-relaxed text-muted-foreground">{entry.flowText}</p>}
        </section>
      )}

      {showSteps && (
        <section aria-labelledby={`help-details-${entry.id}-how`} className="space-y-1.5">
          <h3 id={`help-details-${entry.id}-how`} className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            How to do it
          </h3>
          <ol className="space-y-1.5">
            {entry.steps.map((step, index) => (
              <li key={index} className="flex gap-2.5 leading-relaxed text-foreground">
                <span
                  className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold tabular-nums text-primary"
                  aria-hidden
                >
                  {index + 1}
                </span>
                <span className="min-w-0">
                  <HelpRichText text={step} />
                </span>
              </li>
            ))}
          </ol>
        </section>
      )}

      {entry.keyConcept && (
        <aside className="rounded-lg bg-muted/40 p-3 leading-relaxed text-foreground">
          <span className="font-semibold">Key concept. </span>
          <HelpRichText text={entry.keyConcept} />
        </aside>
      )}
    </div>
  );
}
